import React from 'react';
import { Link } from 'react-router-dom';
import { LUX_CSS, useLuxMotion } from './LuxShared';
import ContactButton from './ContactButton';

const POINTS = [
  ['Idea → shipped MVP', 'You bring the vision. We bring the engineering and ship a real first version people can use.'],
  ['Your fractional technical team', 'Think of us as the technical co-founder you don’t have yet — architecture, build and hard calls, handled.'],
  ['Investor-ready from day one', 'A product and a technical story you can put in front of investors, built to grow past the first raise.'],
  ['You keep everything', 'Clean repo, documented decisions, zero lock-in. The technical spine is yours to own and scale.'],
];

const STEPS = [
  ['01', 'Scope the sketch', 'One working session turns your idea into a scoped roadmap — what ships first, what waits, what "done" looks like for v1.'],
  ['02', 'Build in the open', 'A senior engineer, or a small pod, builds in short, visible sprints. You see the product growing in production, not in a deck, from week one.'],
  ['03', 'Harden for real users', 'Before launch, we pressure-test what breaks — load, security basics, the edges investors and early users find first.'],
  ['04', 'Hand you the keys', 'Documented decisions, a clean repo, zero lock-in — plus the option to keep us on as your technical team through the next raise.'],
];

const WHO = [
  "Founders who've been burned by a freelancer or agency that disappeared after launch",
  'Teams that need an MVP in weeks to get in front of users or investors — not a year-long build',
  'Founders who want a real technical co-founder relationship, not a one-off contractor',
];

const COMPARE = [
  ['Months of recruiting, big equity ask', 'Cheap upfront, disappears after launch', 'Senior engineering from week one'],
  ['No guarantee of fit', 'No documentation, no continuity', 'A real technical partner through your first raise and beyond'],
  ['Slow to start shipping', '—', 'Everything documented and yours'],
];

const FAQ = [
  ["I'm not technical at all — can I still work with you?", 'Yes. Most founders we work with can’t read code. We translate your vision into architecture, scope and a build plan you can follow in plain language.'],
  ['How long does a first version take?', 'Most MVPs ship in weeks, not quarters. The roadmap session at the start defines exactly what "v1 done" means, so there’s no scope creep along the way.'],
  ['Can you help us raise, or just build?', 'We build the product and the technical story — the architecture decisions, the roadmap, the answers investors ask in diligence.'],
  ['What happens after we raise?', 'Many founders keep us on as their fractional technical team through the next stage of growth. Others take the fully documented codebase and hire in-house. Either way, it’s yours.'],
];

const FirstsparkLux = () => {
  const rootRef = useLuxMotion();
  return (
    <div className="fx-root" ref={rootRef}>
      <style>{LUX_CSS}</style>

      <section className="fx-hero fx-mono fx-tall">
        <video className="fx-hero-video" muted loop playsInline autoPlay preload="auto" src="/nh-media/firstspark-hero.mp4" />
        <div className="fx-orb fx-orb-a" />
        <div className="fx-orb fx-orb-b" />
        <div className="fx-hero-scrim" />
        <div className="fx-hero-inner">
          <h1 className="fx-reveal">First<span className="fx-accent">spark.</span></h1>
          <p className="fx-reveal">For founders with a vision and no CTO. We bring the engineering, ship your first version, and give you the technical spine to raise and grow.</p>
          <div className="fx-hero-cta fx-pop">
            <Link className="fx-btn" to="/contact-us">Start building <span className="fx-arr">→</span></Link>
            <Link className="fx-btn fx-ghost" to="/firebucks-catalyst">For enterprises → Catalyst</Link>
          </div>
        </div>
      </section>

      <section className="fx-story">
        <div className="fx-story-inner fx-reveal">
          <span className="fx-story-eyebrow">The idea</span>
          <p>You can see the product clearly. What's missing is the person who can <span className="fx-accent">actually build it</span> — so we become that person, from the first line of code to a version that's ready for users and investors.</p>
        </div>
      </section>

      <section className="fx-section">
        <div className="fx-wrap">
          <div className="fx-lead fx-reveal">
            <h2>The technical team <span className="fx-accent">you don’t have yet.</span></h2>
            <p>Firstspark is the founder-facing door. If you can see the product but can’t build it, we become your engineering — from the first line of code to a version that’s ready for users and investors.</p>
          </div>
          <div className="fx-points">
            {POINTS.map(([h, p]) => (
              <div key={h} className="fx-point fx-reveal"><h4>{h}</h4><p>{p}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="fx-section fx-soft">
        <div className="fx-wrap">
          <div className="fx-head fx-reveal"><h2>How Firstspark <span className="fx-accent">works</span></h2></div>
          <div className="fx-method">
            <div className="fx-rail"><div className="fx-rail-track"><div className="fx-rail-fill" /><div className="fx-rail-pct">0</div></div></div>
            <div className="fx-msteps">
              {STEPS.map(([n, h, p]) => (
                <div key={n} className="fx-mstep fx-reveal">
                  <div className="fx-mstep-head"><span className="fx-mstep-n">/{n}</span><h4>{h}</h4><span className="fx-mstep-plus">+</span></div>
                  <div className="fx-mstep-body"><p>{p}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="fx-section">
        <div className="fx-wrap">
          <div className="fx-head fx-reveal"><h2>Who Firstspark <span className="fx-accent">is for</span></h2></div>
          <div className="fx-checklist">
            {WHO.map((w) => (<div key={w} className="fx-check fx-reveal">{w}</div>))}
          </div>
        </div>
      </section>

      <section className="fx-section fx-soft">
        <div className="fx-wrap">
          <div className="fx-head fx-reveal"><h2>Why founders <span className="fx-accent">choose Firstspark</span></h2></div>
          <div className="fx-compare fx-reveal">
            <table>
              <thead><tr><th>Hiring a CTO</th><th>Freelancers</th><th className="fx-col-win">Firstspark</th></tr></thead>
              <tbody>
                {COMPARE.map((row, i) => (
                  <tr key={i}><td>{row[0]}</td><td>{row[1]}</td><td className="fx-col-win">{row[2]}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="fx-section">
        <div className="fx-wrap">
          <div className="fx-lead fx-reveal">
            <h2>Built on the <span className="fx-accent">same engineering bench</span></h2>
            <p>Firstspark draws on the same senior engineers who deliver inside large, regulated organisations through Firebucks Catalyst — pointed at your first version instead of a Fortune 500 programme.</p>
          </div>
        </div>
      </section>

      <section className="fx-section fx-soft">
        <div className="fx-wrap">
          <div className="fx-head fx-reveal"><h2>Questions, <span className="fx-accent">answered</span></h2></div>
          <div className="fx-faq">
            {FAQ.map(([q, a]) => (
              <div key={q} className="fx-faq-item fx-reveal">
                <div className="fx-faq-q">{q}<span className="fx-faq-plus">+</span></div>
                <div className="fx-faq-a"><p>{a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactButton
        title="Have an idea and no technical team?"
        subtitle="Tell us what you're trying to build. We'll bring the spark."
      />
    </div>
  );
};

export default FirstsparkLux;
